/**
 * ViewportContextMenu - PACS-style right-click context menu for viewports
 *
 * Shows all shortcut groups as a cascading menu. Hovering a group opens
 * a flyout submenu with individual actions and their keyboard shortcuts.
 * Clicking an actionable item dispatches the corresponding keyboard event
 * so all existing hotkey handlers fire as if the user pressed the key.
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useAtomValue, useSetAtom } from "jotai";
import {
  SHORTCUT_GROUPS,
  filterShortcutGroupsBySurfaces,
  type ShortcutGroup,
  type ShortcutItem,
} from "../../constants/viewerActions";
import { viewerSurfacesAtom } from "../../atoms/viewerSurfacesAtom";
import { clearAllMeasurementsConfirmAtom } from "../../atoms/measurementAtoms";
import { activeSlotAtom } from "../../atoms/hangingProtocolAtoms";
import { findHangingProtocolViewport } from "../../utils/dicomLoader";
import { runCommand } from "../../lib/commands";
import { emit as busEmit } from "../../lib/viewerBus";
import { PLANE_COLORS_HEX } from "../../constants/planeColors";
import { logger } from "../../utils/logger";
import type { PlaneOrientation } from "../../viewport/types";

// Sidebar's SeriesReformatMenu is the wording/color source of truth — kept in
// sync deliberately, this menu is just a second entry point to the same intent.
// All three planes; the offered subset excludes the source series' own native
// plane (computed per-render below from `reformat.sourcePlane`).
const ALL_PLANES: { plane: PlaneOrientation; color: string }[] = [
  { plane: "Axial", color: PLANE_COLORS_HEX.axial },
  { plane: "Coronal", color: PLANE_COLORS_HEX.coronal },
  { plane: "Sagittal", color: PLANE_COLORS_HEX.sagittal },
];

// ── Action dispatch ─────────────────────────────────────────────────────────
// Maps actionId → KeyboardEvent init so we can fire the same event the
// keyboard shortcut would. This keeps the context menu 100% in sync with
// the existing hotkey handlers without duplicating callback logic.

const ACTION_KEY_MAP: Record<string, KeyboardEventInit> = {
  // Viewer Controls
  tool_wl: { key: "w", code: "KeyW" },
  tool_pan: { key: "p", code: "KeyP" },
  tool_zoom: { key: "z", code: "KeyZ" },
  reset_view: { key: "r", code: "KeyR" },
  invert: { key: "i", code: "KeyI" },
  toggle_reflines: { key: "x", code: "KeyX" },

  // Case Navigation
  prev_case: { key: "ArrowLeft", code: "ArrowLeft" },
  next_case: { key: "ArrowRight", code: "ArrowRight" },
  next_unlabeled: { key: "n", code: "KeyN" },

  // Measurement Tools
  tool_ruler: { key: "U", code: "KeyU", shiftKey: true },
  tool_roi: { key: "M", code: "KeyM", shiftKey: true },
  tool_length: { key: "l", code: "KeyL" },
  escape: { key: "Escape", code: "Escape" },
  delete: { key: "Delete", code: "Delete" },

  // Segmentation Tools
  tool_lasso: { key: "S", code: "KeyS", shiftKey: true },
  tool_bump: { key: "E", code: "KeyE", shiftKey: true },
  tool_regiongrow: { key: "G", code: "KeyG", shiftKey: true },
  undo: { key: "z", code: "KeyZ", ctrlKey: true, metaKey: true },

  // Window Presets (bare number keys — no shift)
  preset_soft_tissue: { key: "1", code: "Digit1" },
  preset_lung: { key: "2", code: "Digit2" },
  preset_bone: { key: "3", code: "Digit3" },
  preset_brain: { key: "4", code: "Digit4" },
  preset_liver: { key: "5", code: "Digit5" },

  // Layout
  toggle_left_sidebar: { key: ",", code: "Comma" },
  toggle_right_sidebar: { key: ".", code: "Period" },
};

function dispatchAction(actionId: string) {
  const init = ACTION_KEY_MAP[actionId];
  if (!init) return;
  const event = new KeyboardEvent("keydown", {
    ...init,
    bubbles: true,
    cancelable: true,
  });
  // Dispatch on document so both window.addEventListener and react-hotkeys-hook pick it up
  document.dispatchEvent(event);
}

// ── Positioning helpers ─────────────────────────────────────────────────────

const MENU_WIDTH = 220;
const FLYOUT_MAX_WIDTH = Math.round(MENU_WIDTH * 1.5);
const ITEM_HEIGHT = 28;
const MENU_PADDING = 8;

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

/** Compute initial top-level menu position (left only — top is adjusted after mount). */
function getMenuLeft(x: number) {
  return clamp(x, 4, window.innerWidth - MENU_WIDTH - 4);
}

/** Compute flyout position from the hovered row's actual DOM rect.
 *  The flyout's top edge aligns with the hovered group row. */
export function getFlyoutPosition(
  rowRect: DOMRect,
  menuLeft: number,
  itemCount: number,
): { top: number; left?: number; right?: number } {
  const flyoutHeight = itemCount * ITEM_HEIGHT + MENU_PADDING * 2;
  // Top edge aligns with the hovered row, clamped to viewport
  const top = clamp(rowRect.top, 4, window.innerHeight - flyoutHeight - 4);

  // Prefer right side; flip left if no room.
  const overflowsRight = menuLeft + MENU_WIDTH + FLYOUT_MAX_WIDTH + 4 > window.innerWidth;
  if (overflowsRight) {
    // Anchor by the flyout's RIGHT edge so it sits flush 2px to the left of the
    // parent menu's left edge regardless of the flyout's actual (maxWidth-capped)
    // width — left-anchoring by FLYOUT_MAX_WIDTH left a narrower flyout detached.
    return { right: window.innerWidth - menuLeft + 2, top };
  }
  return { left: menuLeft + MENU_WIDTH + 2, top };
}

// ── Component ───────────────────────────────────────────────────────────────

interface ViewportContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  /** Set when this pane can host a reformat request: the series to reformat,
   *  why it can't be (null = it can), and its own native plane (excluded from
   *  the offered planes — null when the plane can't be derived, offering all
   *  three). Omitted entirely on surfaces where reformats don't apply, so the
   *  menu renders no Reformat row at all. */
  reformat?: { seriesId: string; blocker: string | null; sourcePlane: PlaneOrientation | null };
}

export const ViewportContextMenu: React.FC<ViewportContextMenuProps> = ({
  x,
  y,
  onClose,
  reformat,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const groupRowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const reformatRowRef = useRef<HTMLDivElement | null>(null);
  const [hoveredGroup, setHoveredGroup] = useState<number | null>(null);
  const [reformatHovered, setReformatHovered] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [menuTop, setMenuTop] = useState(y);
  const menuLeft = getMenuLeft(x);
  const activeSurfaces = useAtomValue(viewerSurfacesAtom);
  const setClearAllConfirm = useSetAtom(clearAllMeasurementsConfirmAtom);
  const activeSlot = useAtomValue(activeSlotAtom);
  const groups = useMemo(
    () => filterShortcutGroupsBySurfaces(SHORTCUT_GROUPS, activeSurfaces),
    [activeSurfaces],
  );
  // Every plane except the source series' own native one — "reformat this
  // axial to axial" is not a request anyone can make. `sourcePlane === null`
  // (genuinely undeterminable) offers all three rather than guessing.
  const offeredPlanes = useMemo(
    () => ALL_PLANES.filter(({ plane }) => plane !== reformat?.sourcePlane),
    [reformat?.sourcePlane],
  );

  // Most menu items fire by dispatching their keyboard event (keeps the menu in
  // sync with the hotkey handlers). The two measurement-clear actions are the
  // exception: their key (Shift+Delete) is context-overloaded — when a
  // segmentation is in progress it wipes contours, not measurements — so a menu
  // item labelled "clear measurements" must invoke the command directly instead
  // of blindly dispatching that key.
  const runMenuAction = useCallback(
    (actionId: string) => {
      if (actionId === "clear_all_measurements") {
        setClearAllConfirm(true);
        return;
      }
      if (actionId === "clear_slice_measurements") {
        const vp = findHangingProtocolViewport(activeSlot);
        const imageId = vp?.getCurrentImageId?.() ?? null;
        if (!imageId) {
          logger.warn("[ViewportContextMenu] clear-slice skipped: no current imageId");
          return;
        }
        runCommand("clearMeasurementsOnSlice", { imageId });
        return;
      }
      dispatchAction(actionId);
    },
    [setClearAllConfirm, activeSlot],
  );

  // After mount, measure actual height and adjust top so menu doesn't overflow
  useEffect(() => {
    if (menuPanelRef.current) {
      const height = menuPanelRef.current.getBoundingClientRect().height;
      setMenuTop(clamp(y, 4, window.innerHeight - height - 4));
    }
  }, [y]);

  // Close on Escape or click outside
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleScroll = () => onClose();

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("mousedown", handleClick, true);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("mousedown", handleClick, true);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [onClose]);

  // Cleanup hover timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  const handleGroupHover = useCallback((index: number) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    // A group flyout and the Reformat flyout are never open together.
    setReformatHovered(false);
    hoverTimerRef.current = setTimeout(() => setHoveredGroup(index), 80);
  }, []);

  const handleGroupLeave = useCallback(() => {
    // Don't clear immediately — the user may be moving to the flyout
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setHoveredGroup(null), 200);
  }, []);

  const handleReformatHover = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    // The Reformat flyout and a group flyout are never open together.
    setHoveredGroup(null);
    hoverTimerRef.current = setTimeout(() => setReformatHovered(true), 80);
  }, []);

  const handleReformatLeave = useCallback(() => {
    // Don't clear immediately — the user may be moving to the flyout
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setReformatHovered(false), 200);
  }, []);

  const handleFlyoutEnter = useCallback(() => {
    // Cancel the pending close when mouse enters the flyout
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, []);

  const handleItemClick = useCallback(
    (item: ShortcutItem) => {
      if (!item.actionId) return;
      onClose();
      // Small delay so the menu closes visually before the action fires
      requestAnimationFrame(() => runMenuAction(item.actionId!));
    },
    [onClose, runMenuAction],
  );

  // Portal to document.body so the menu isn't clipped by any parent overflow/positioning
  return createPortal(
    <div ref={menuRef} className="fixed inset-0 z-9999 pointer-events-none">
      {/* Top-level group menu */}
      <div
        ref={menuPanelRef}
        className="absolute pointer-events-auto bg-black border border-border-strong rounded-md shadow-xl py-1 select-none"
        style={{ left: menuLeft, top: menuTop, width: MENU_WIDTH }}
      >
        {groups.map((group, idx) => (
          <React.Fragment key={group.category}>
            {idx > 0 && groups[idx - 1].section !== group.section && (
              <div className="border-t border-border-strong my-1" />
            )}
            <div
              ref={(el) => {
                groupRowRefs.current[idx] = el;
              }}
              className={`flex items-center justify-between px-3 h-7 text-sm cursor-default transition-colors ${
                hoveredGroup === idx ? "bg-surface-2 text-white" : "text-fg hover:bg-surface-2/50"
              }`}
              onMouseEnter={() => handleGroupHover(idx)}
              onMouseLeave={handleGroupLeave}
            >
              <span>{group.category}</span>
              <span className="text-fg-muted text-xs ml-2">{"\u25B8"}</span>
            </div>
          </React.Fragment>
        ))}

        {/* Quick-action: Reformat — same cascading-hover idiom as the groups
            above, a second entry point for the same request the sidebar's
            SeriesReformatMenu makes. Not folded into a synthetic ShortcutGroup:
            its rows are plane-colored dots with no keyboard shortcut and
            per-item disabled state, which ShortcutItem doesn't model. */}
        {reformat && (
          <>
            <div className="border-t border-border-strong my-1" />
            <div
              ref={reformatRowRef}
              className={`flex items-center justify-between px-3 h-7 text-sm cursor-default transition-colors ${
                reformatHovered ? "bg-surface-2 text-white" : "text-fg hover:bg-surface-2/50"
              }`}
              onMouseEnter={handleReformatHover}
              onMouseLeave={handleReformatLeave}
            >
              <span>Reformat</span>
              <span className="text-fg-muted text-xs ml-2">{"▸"}</span>
            </div>
          </>
        )}

        {/* Quick-action: Delete / Clear Annotation */}
        <div className="border-t border-border-strong my-1" />
        <div
          className="flex items-center justify-between px-3 h-7 text-sm text-warn hover:bg-surface-2 cursor-pointer"
          onMouseEnter={() => {
            if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
            setHoveredGroup(null);
            setReformatHovered(false);
          }}
          onClick={() => {
            onClose();
            requestAnimationFrame(() => dispatchAction("delete"));
          }}
        >
          <span>Clear Annotation</span>
          <kbd className="shrink-0 px-1.5 py-0.5 bg-white/10 rounded text-fg-muted font-mono text-[10px] leading-tight whitespace-nowrap">
            Del
          </kbd>
        </div>
      </div>

      {/* Reformat flyout submenu */}
      {reformatHovered && reformat && reformatRowRef.current && (
        <ReformatFlyout
          seriesId={reformat.seriesId}
          blocker={reformat.blocker}
          planes={offeredPlanes}
          position={getFlyoutPosition(
            reformatRowRef.current.getBoundingClientRect(),
            menuLeft,
            offeredPlanes.length,
          )}
          onClose={onClose}
          onMouseEnter={handleFlyoutEnter}
          onMouseLeave={handleReformatLeave}
        />
      )}

      {/* Flyout submenu */}
      {hoveredGroup !== null && groups[hoveredGroup] && groupRowRefs.current[hoveredGroup] && (
        <FlyoutSubmenu
          group={groups[hoveredGroup]}
          position={getFlyoutPosition(
            groupRowRefs.current[hoveredGroup]!.getBoundingClientRect(),
            menuLeft,
            groups[hoveredGroup].items.length,
          )}
          onItemClick={handleItemClick}
          onMouseEnter={handleFlyoutEnter}
          onMouseLeave={handleGroupLeave}
        />
      )}
    </div>,
    document.body,
  );
};

// ── Flyout Submenu ──────────────────────────────────────────────────────────

interface FlyoutSubmenuProps {
  group: ShortcutGroup;
  position: { top: number; left?: number; right?: number };
  onItemClick: (item: ShortcutItem) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const FlyoutSubmenu: React.FC<FlyoutSubmenuProps> = ({
  group,
  position,
  onItemClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [adjustedTop, setAdjustedTop] = useState(position.top);

  // Measure actual height after render and clamp to viewport
  useEffect(() => {
    if (flyoutRef.current) {
      const height = flyoutRef.current.getBoundingClientRect().height;
      setAdjustedTop(clamp(position.top, 4, window.innerHeight - height - 4));
    }
  }, [position.top, group]);

  return (
    <div
      ref={flyoutRef}
      className="absolute pointer-events-auto bg-black border border-border-strong rounded-md shadow-xl py-1 select-none"
      style={{
        left: position.left,
        right: position.right,
        top: adjustedTop,
        maxWidth: FLYOUT_MAX_WIDTH,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {group.items.map((item) => {
        const isActionable = !!item.actionId;
        return (
          <div
            key={`${item.key}-${item.description}`}
            className={`flex items-center justify-between px-3 min-h-[28px] py-1 text-sm gap-4 ${
              isActionable
                ? "text-fg hover:bg-surface-2 cursor-pointer"
                : "text-fg-muted cursor-default"
            }`}
            onClick={() => isActionable && onItemClick(item)}
          >
            <span>{item.description}</span>
            {item.key === "Delete / Backspace" ? (
              <kbd className="shrink-0 px-1.5 py-0.5 bg-white/10 rounded text-fg-muted font-mono text-[10px] leading-relaxed text-center">
                Delete /<br />
                Backspace
              </kbd>
            ) : (
              <kbd className="shrink-0 px-1.5 py-0.5 bg-white/10 rounded text-fg-muted font-mono text-[10px] leading-relaxed whitespace-nowrap">
                {item.key}
              </kbd>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Reformat Flyout ─────────────────────────────────────────────────────────
// Styled to match FlyoutSubmenu, but the rows are different enough (plane dot,
// no keyboard shortcut, per-item disabled state) that forcing them into a
// synthetic ShortcutGroup/ShortcutItem would be the wrong shape.

interface ReformatFlyoutProps {
  seriesId: string;
  blocker: string | null;
  planes: { plane: PlaneOrientation; color: string }[];
  position: { top: number; left?: number; right?: number };
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const ReformatFlyout: React.FC<ReformatFlyoutProps> = ({
  seriesId,
  blocker,
  planes,
  position,
  onClose,
  onMouseEnter,
  onMouseLeave,
}) => {
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [adjustedTop, setAdjustedTop] = useState(position.top);

  // Measure actual height after render and clamp to viewport
  useEffect(() => {
    if (flyoutRef.current) {
      const height = flyoutRef.current.getBoundingClientRect().height;
      setAdjustedTop(clamp(position.top, 4, window.innerHeight - height - 4));
    }
  }, [position.top, planes.length]);

  return (
    <div
      ref={flyoutRef}
      className="absolute pointer-events-auto bg-black border border-border-strong rounded-md shadow-xl py-1 select-none"
      style={{
        left: position.left,
        right: position.right,
        top: adjustedTop,
        maxWidth: FLYOUT_MAX_WIDTH,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {planes.map(({ plane, color }) => (
        <button
          key={plane}
          type="button"
          role="menuitem"
          disabled={!!blocker}
          className="flex w-full items-center gap-2 px-3 h-7 text-sm text-left text-fg hover:bg-surface-2 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:text-fg-muted"
          onClick={() => {
            onClose();
            requestAnimationFrame(() => busEmit("seriesReformatRequest", { seriesId, plane }));
          }}
        >
          <span
            className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
            style={{ backgroundColor: color }}
          />
          {plane} reformat
        </button>
      ))}
      {blocker && (
        <div className="px-3 py-1 text-[10px] leading-tight text-warn">
          Cannot reformat: {blocker}
        </div>
      )}
    </div>
  );
};

export default ViewportContextMenu;
